import { Link } from "@tanstack/react-router";
import type React from "react";
import type { ReactNode } from "react";
import { tv } from "tailwind-variants";
import { cn } from "@/lib/utils";

const listItemVariants = tv({
  base: [
    "flex h-[34px] w-[231px] items-center",
    "min-h-[48px] w-full py-1 pr-[27px]",
    "text-left text-[12pt] font-bold transition-colors",
    "rounded-[0px_17px_0px_0px]",
  ],
  variants: {
    state: {
      default: "text-variable-collection-dark-gray hover:bg-study-group-hover",
      active: "bg-variable-collection-main text-custom-white",
      selected: "bg-study-group-selected text-white",
    },
    density: {
      default: "",
      compact: "h-[56px] min-h-[56px] py-1 pr-3",
    },
  },
  defaultVariants: {
    state: "default",
    density: "default",
  },
});

const labelVariants = tv({
  base: ["max-w-full text-xs leading-[1.2]"],
  variants: {
    clamp: {
      single: "truncate",
      multi: "line-clamp-2 break-words",
    },
  },
  defaultVariants: {
    clamp: "multi",
  },
});

const sublabelVariants = tv({
  base: "truncate text-xs",
  variants: {
    state: {
      default: "text-gray-500",
      active: "text-gray-500",
      selected: "text-white",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

interface ListItemProps {
  icon: ReactNode;
  label: string;
  sublabel?: ReactNode;
  sublabelRightContent?: ReactNode;
  labelSingleLine?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  to?: string;
  className?: string;
  selected?: boolean;
  rightContent?: ReactNode;
  density?: "default" | "compact";
  labelClassName?: string;
  sublabelClassName?: string;
  iconWrapperClassName?: string;
  selectedVariant?: "fill" | "bar";
}

interface ListContainerProps {
  header: ReactNode;
  className?: string;
  containerHeight?: string | number;
  children: ReactNode;
  footer?: ReactNode;
  scrollContainerRef?: React.Ref<HTMLDivElement>;
  dividerClassName?: string;
  scrollClassName?: string;
}

/**
 * リストアイテムコンポーネント - ボタンまたはLinkとして機能
 */
export const ListItem: React.FC<ListItemProps> = ({
  icon,
  label,
  sublabel,
  sublabelRightContent,
  labelSingleLine = false,
  isActive = false,
  onClick,
  to,
  className = "",
  selected = false,
  rightContent,
  density = "default",
  labelClassName,
  sublabelClassName,
  iconWrapperClassName,
  selectedVariant = "fill",
}) => {
  // 状態の決定
  const state = selected ? "selected" : isActive ? "active" : "default";
  const styleState = selected && selectedVariant === "bar" ? "default" : state;

  const content = (
    <>
      <span
        className={cn(
          "flex h-12 w-12 flex-shrink-0 items-center justify-center",
          iconWrapperClassName,
        )}
      >
        {icon}
      </span>
      <div className="flex min-w-0 grow flex-col">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <span
            className={cn(
              labelVariants({ clamp: labelSingleLine ? "single" : "multi" }),
              labelClassName,
            )}
          >
            {label}
          </span>
          {rightContent ? (
            <div className="flex shrink-0 items-center gap-1">
              {rightContent}
            </div>
          ) : null}
        </div>
        {sublabel || sublabelRightContent ? (
          <div className="flex min-w-0 items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              {sublabel ? (
                typeof sublabel === "string" ? (
                  <span
                    className={cn(
                      "block",
                      sublabelVariants({ state: styleState }),
                      sublabelClassName,
                    )}
                  >
                    {sublabel}
                  </span>
                ) : (
                  <div
                    className={cn(
                      "min-w-0",
                      sublabelVariants({ state: styleState }),
                      sublabelClassName,
                    )}
                  >
                    {sublabel}
                  </div>
                )
              ) : (
                <span
                  className={cn(
                    "block",
                    sublabelVariants({ state: styleState }),
                    sublabelClassName,
                  )}
                />
              )}
            </div>
            {sublabelRightContent ? (
              <div className="flex shrink-0 items-center gap-1">
                {sublabelRightContent}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );

  const combinedClassName = cn(
    listItemVariants({ state: styleState, density, className }),
    selected && selectedVariant === "bar"
      ? "relative bg-study-group-hover/90"
      : "",
  );
  const selectionBar =
    selected && selectedVariant === "bar" ? (
      <span
        aria-hidden="true"
        className="absolute left-0 top-2 h-[calc(100%-16px)] w-[3px] rounded-r bg-variable-collection-main"
      />
    ) : null;

  // toプロパティが指定されている場合はリンクとして、そうでない場合はボタンとしてレンダリング
  if (to) {
    return (
      <Link to={to} className={combinedClassName}>
        {selectionBar}
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={combinedClassName}
      title={label}
    >
      {selectionBar}
      {content}
    </button>
  );
};

/**
 * リストコンテナコンポーネント
 */
export const ListContainer: React.FC<ListContainerProps> = ({
  header,
  children,
  className = "",
  containerHeight = "450px",
  footer,
  scrollContainerRef,
  dividerClassName,
  scrollClassName,
}) => {
  // 高さの値を適用するためのスタイルオブジェクト
  const containerStyle = {
    height:
      typeof containerHeight === "number"
        ? `${containerHeight}px`
        : containerHeight,
  };

  return (
    <div
      className={`flex w-full flex-col rounded-[0px_17px_0px_0px] bg-custom-white p-1.5 ${className}`}
      style={containerStyle}
    >
      {/* ヘッダー部分 */}
      {header}

      <hr
        className={cn(
          "mb-2 rounded-full border-t-2 border-variable-collection-light-gray",
          dividerClassName,
        )}
      />

      {/* スクロール可能なコンテナ */}
      <div
        ref={scrollContainerRef}
        className={cn(
          "flex min-h-0 flex-grow flex-col",
          scrollClassName ?? "overflow-y-auto",
        )}
      >
        {children}
      </div>

      {footer ? <div className="shrink-0 pt-2">{footer}</div> : null}
    </div>
  );
};
